
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BlogPost } from "./BlogPostCard";

export interface BlogFeaturedPostProps {
  post: BlogPost;
}

export function BlogFeaturedPost({ post }: BlogFeaturedPostProps) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  
  // Format date to localized string based on current language
  const formattedDate = new Date(post.date).toLocaleDateString(
    i18n.language === 'ja' ? 'ja-JP' : 'en-US', 
    {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }
  );

  return (
    <Card className="overflow-hidden border-0 shadow-lg bg-gradient-to-br from-white/50 to-white/30 dark:from-black/20 dark:to-black/10 border-gray-200/50 dark:border-gray-800/50 backdrop-blur-sm">
      <CardContent className="p-0">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="relative h-full min-h-[280px] overflow-hidden bg-muted">
            <Badge 
              variant="gradient" 
              className="absolute top-4 left-4 z-10"
            >
              {t('blog.featured')}
            </Badge>
            <img 
              src={post.image} 
              alt={post.title}
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
            />
          </div>
          <div className="flex flex-col justify-center p-6 space-y-4">
            <div className="flex items-center space-x-4">
              <Badge variant="outline" className="bg-primary/10 text-primary hover:bg-primary/20">
                {post.category}
              </Badge>
              <div className="flex items-center text-sm text-muted-foreground">
                <Calendar className="h-3 w-3 mr-1" />
                {formattedDate}
              </div>
            </div>
            
            <h2 className="text-3xl font-bold bg-gradient-to-r from-primary to-green bg-clip-text text-transparent">
              {post.title}
            </h2>
            
            <p className="text-muted-foreground">
              {post.description}
            </p>
            
            <div className="flex justify-between items-center pt-4">
              <span className="font-medium">{post.author || ''}</span>
              <Button 
                onClick={() => navigate(`/blog/${post.id}`)}
                className="group"
              >
                {t('blog.readArticle')}
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
